import { useState, useCallback, lazy, Suspense } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { usePublicQuote } from '@/hooks/usePublicQuote';
import { publicQuotesApi } from '@/services/public-quotes';
import { MaterialCard } from '@/components/upload/MaterialCard';
import { ColorSwatch } from '@/components/quote/ColorSwatch';
import { PublicCheckoutPanel } from '@/components/shared-quotes/PublicCheckoutPanel';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import type { PublicMaterial } from '@/services/materials';

const ModelViewer = lazy(() =>
  import('@/components/upload/ModelViewer').then((m) => ({ default: m.ModelViewer })),
);

export const PublicQuotePage = () => {
  const { token } = useParams<{ token: string }>();
  const { user, isAuthenticated } = useAuth();
  const { data: quote, isLoading, error } = usePublicQuote(token);
  const [selectedMaterialId, setSelectedMaterialId] = useState<string | null>(null);
  const [selectedColor, setSelectedColor] = useState<string | null>(null);

  // Fetch presigned preview URL for 3D model
  const is3mf = quote?.fileName?.toLowerCase().endsWith('.3mf');
  const { data: previewUrl } = useQuery({
    queryKey: ['public-quote-preview', token],
    queryFn: () => publicQuotesApi.getPreviewUrl(token!),
    enabled: !!token && !!quote && !!is3mf,
    staleTime: 10 * 60 * 1000, // 10 min (URL expires in 15)
    retry: false,
  });

  const handleSelectMaterial = useCallback((materialId: string) => {
    setSelectedMaterialId(materialId);
    setSelectedColor(null);
  }, []);

  const handleSelectColor = useCallback((color: string) => {
    setSelectedColor(color);
  }, []);

  if (isLoading) {
    return (
      <div
        className="container-main"
        style={{
          maxWidth: '800px',
          paddingTop: 'var(--space-16)',
          paddingBottom: 'var(--space-16)',
          textAlign: 'center',
        }}
      >
        <LoadingSpinner size="lg" />
        <p
          style={{
            marginTop: 'var(--space-4)',
            color: 'var(--text-secondary)',
            fontSize: 'var(--text-sm)',
          }}
        >
          Loading quote...
        </p>
      </div>
    );
  }

  if (error || !quote) {
    const message = error instanceof Error ? error.message : 'Quote not found';
    return (
      <div
        className="container-main"
        style={{
          maxWidth: '600px',
          paddingTop: 'var(--space-16)',
          paddingBottom: 'var(--space-16)',
          textAlign: 'center',
        }}
      >
        <div
          style={{
            borderRadius: 'var(--radius-xl)',
            border: '1px solid var(--border-default)',
            backgroundColor: 'var(--bg-surface)',
            padding: 'var(--space-8)',
          }}
        >
          <div
            style={{
              width: '3rem',
              height: '3rem',
              borderRadius: 'var(--radius-full)',
              backgroundColor: 'var(--bg-error-subtle)',
              color: 'var(--color-error-700)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto var(--space-4)',
            }}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} style={{ width: '1.5rem', height: '1.5rem' }}>
              <circle cx="12" cy="12" r="10" />
              <line x1="15" y1="9" x2="9" y2="15" />
              <line x1="9" y1="9" x2="15" y2="15" />
            </svg>
          </div>
          <h1
            style={{
              fontSize: 'var(--text-xl)',
              fontWeight: 'var(--font-weight-bold)',
              color: 'var(--text-primary)',
              marginBottom: 'var(--space-2)',
            }}
          >
            Quote Unavailable
          </h1>
          <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-sm)' }}>
            {message}
          </p>
        </div>
      </div>
    );
  }

  // Adapt materialOptions to PublicMaterial shape for ColorSwatch
  const materialsForSwatch: readonly PublicMaterial[] = quote.materialOptions.map((m) => ({
    materialId: m.materialId,
    name: m.name,
    description: m.description,
    colorOptions: m.colorOptions,
    colorStock: Object.fromEntries(m.colorOptions.map((c) => [c, true])),
  }));

  const selectedMaterial = selectedMaterialId
    ? quote.materialOptions.find((m) => m.materialId === selectedMaterialId)
    : null;
  const selectedPrice = selectedMaterialId
    ? quote.prices.find((p) => p.materialId === selectedMaterialId)
    : null;

  const expiresDate = new Date(quote.expiresAt);
  const isExpired = expiresDate.getTime() < Date.now();

  return (
    <div
      className="container-main"
      style={{
        maxWidth: '1100px',
        paddingTop: 'var(--space-12)',
        paddingBottom: 'var(--space-12)',
      }}
    >
      {/* Header */}
      <div style={{ marginBottom: 'var(--space-8)' }}>
        <h1
          style={{
            fontSize: 'var(--text-3xl)',
            fontWeight: 'var(--font-weight-bold)',
            color: 'var(--text-primary)',
            letterSpacing: 'var(--tracking-tight)',
          }}
        >
          {quote.title}
        </h1>
        <p
          style={{
            marginTop: 'var(--space-2)',
            color: 'var(--text-secondary)',
          }}
        >
          {quote.customerName
            ? `Prepared for ${quote.customerName}. Select your material and color to order.`
            : 'Select your material and color to proceed to checkout.'}
        </p>
      </div>

      {/* Admin message */}
      {quote.message && (
        <div
          style={{
            marginBottom: 'var(--space-6)',
            borderRadius: 'var(--radius-lg)',
            backgroundColor: 'var(--bg-surface-sunken)',
            padding: 'var(--space-4)',
            fontSize: 'var(--text-sm)',
            color: 'var(--text-secondary)',
            lineHeight: 'var(--leading-relaxed)',
            whiteSpace: 'pre-wrap',
          }}
        >
          {quote.message}
        </div>
      )}

      {/* Expired notice */}
      {isExpired && (
        <div
          style={{
            marginBottom: 'var(--space-6)',
            borderRadius: 'var(--radius-lg)',
            backgroundColor: 'var(--bg-error-subtle)',
            padding: 'var(--space-4)',
            fontSize: 'var(--text-sm)',
            color: 'var(--color-error-700)',
          }}
        >
          This quote has expired and is no longer available for purchase.
        </div>
      )}

      {!isExpired && (
        <div className="quote-layout">
          {/* Left column -- configuration */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-8)' }}>
            {/* Model stats grid */}
            <div className="grid grid-cols-2 sm:grid-cols-4" style={{ gap: 'var(--space-4)' }}>
              <StatBox label="Volume" value={`${quote.volumeCm3} cm\u00B3`} />
              <StatBox label="Surface Area" value={`${quote.surfaceAreaCm2} cm\u00B2`} />
              <StatBox
                label="Dimensions"
                value={`${quote.boundingBox.width} x ${quote.boundingBox.height} x ${quote.boundingBox.depth} cm`}
              />
              <StatBox
                label="File"
                value={quote.fileName}
              />
            </div>

            {/* 3D Preview */}
            {previewUrl && (
              <div>
                <SectionHeading title="3D Preview" />
                <Suspense fallback={<LoadingSpinner size="lg" />}>
                  <ModelViewer
                    url={previewUrl}
                    color={selectedColor}
                    className="h-[400px]"
                  />
                </Suspense>
              </div>
            )}

            {/* Material selection */}
            <div>
              <SectionHeading title="Select a Material" />
              <div className="grid sm:grid-cols-2 lg:grid-cols-3" style={{ gap: 'var(--space-4)' }}>
                {quote.prices.map((price) => {
                  const mat = quote.materialOptions.find(
                    (m) => m.materialId === price.materialId,
                  );
                  if (!mat) return null;

                  return (
                    <MaterialCard
                      key={price.materialId}
                      materialId={price.materialId}
                      name={mat.name}
                      description={mat.description}
                      price={price.price}
                      estimatedDays={price.estimatedDays}
                      isSelected={selectedMaterialId === price.materialId}
                      onSelect={handleSelectMaterial}
                    />
                  );
                })}
              </div>
            </div>

            {/* Color selection -- shown after material is selected */}
            {selectedMaterialId && (
              <div>
                <SectionHeading title="Choose Your Color" />
                <ColorSwatch
                  materials={materialsForSwatch}
                  selectedMaterialId={selectedMaterialId}
                  selectedColor={selectedColor}
                  onSelectColor={handleSelectColor}
                />
              </div>
            )}
          </div>

          {/* Right column -- sticky checkout panel */}
          <div className="quote-sidebar">
            <div className="quote-sidebar-sticky">
              <PublicCheckoutPanel
                token={token ?? ''}
                title={quote.title}
                volumeCm3={quote.volumeCm3}
                materialId={selectedMaterialId}
                materialName={selectedMaterial?.name ?? null}
                color={selectedColor}
                price={selectedPrice?.price ?? null}
                rawPrice={selectedPrice?.rawPrice ?? null}
                estimatedDays={selectedPrice?.estimatedDays ?? null}
                boundingBox={quote.boundingBox}
                userEmail={isAuthenticated ? user?.email : null}
              />
            </div>
          </div>
        </div>
      )}

      {/* Expiration notice */}
      {!isExpired && (
        <p
          style={{
            marginTop: 'var(--space-8)',
            fontSize: 'var(--text-xs)',
            color: 'var(--text-tertiary)',
            textAlign: 'center',
          }}
        >
          This quote is valid until{' '}
          {expiresDate.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
          })}
          .
        </p>
      )}
    </div>
  );
};

/* ---- helpers ---- */

const SectionHeading = ({ title }: { readonly title: string }) => (
  <h3
    style={{
      marginBottom: 'var(--space-4)',
      fontSize: 'var(--text-lg)',
      fontWeight: 'var(--font-weight-semibold)',
      color: 'var(--text-primary)',
    }}
  >
    {title}
  </h3>
);

const StatBox = ({ label, value }: { readonly label: string; readonly value: string }) => (
  <div
    style={{
      borderRadius: 'var(--radius-lg)',
      border: '1px solid var(--border-default)',
      backgroundColor: 'var(--bg-surface)',
      padding: 'var(--space-3)',
    }}
  >
    <p
      style={{
        fontSize: 'var(--text-xs)',
        fontWeight: 'var(--font-weight-medium)',
        textTransform: 'uppercase',
        letterSpacing: 'var(--tracking-wide)',
        color: 'var(--text-tertiary)',
      }}
    >
      {label}
    </p>
    <p
      className="truncate"
      style={{
        marginTop: 'var(--space-1)',
        fontSize: 'var(--text-sm)',
        fontWeight: 'var(--font-weight-semibold)',
        color: 'var(--text-primary)',
      }}
    >
      {value}
    </p>
  </div>
);
